import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../data/supabaseClient';
import { UserBadge, UserBadgeGroup, BADGE_CONFIG } from '../common/UserBadge';
import { LuRefreshCw } from "react-icons/lu";
import Spinner from '../common/Spinner';
import { useToast } from '../hooks/ToastContext';
import './MyProfile.css';

const UserBadges = ({ userId }) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  
  const [userBadges, setUserBadges] = useState([]);
  const [badgeProgress, setBadgeProgress] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  // Fetch user's badges and calculate progress
  useEffect(() => {
    const fetchUserBadges = async () => {
      if (!userId) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch user's earned badges
        const { data: badgesData, error: badgesError } = await supabase
          .from('user_badges')
          .select('*')
          .eq('user_id', userId)
          .eq('is_active', true)
          .order('earned_at', { ascending: false });
          
        if (badgesError) {
          console.error('Error fetching badges:', badgesError);
          throw new Error('Failed to fetch badges');
        }
        
        setUserBadges(badgesData || []);
        
        // Calculate badge progress
        await calculateBadgeProgress(userId);
        
      } catch (err) {
        console.error('Error in fetchUserBadges:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserBadges();
  }, [userId]);

  // Calculate progress towards unearned badges
  const calculateBadgeProgress = async (userId) => {
    try {
      // Get user stats for calculations
      const { data: stats, error: statsError } = await supabase
        .from('user_stats')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (statsError && statsError.code !== 'PGRST116') {
        console.error('Error fetching stats for badge progress:', statsError);
        return;
      }
      
      // Get posts and packages count
      const { data: postsData } = await supabase
        .from('posts')
        .select('id, role, address_to_city')
        .eq('user_id', userId);
        
      const { data: packagesData } = await supabase
        .from('packages')
        .select('id')
        .eq('user_id', userId);
        
      // Get completed rides
      const { data: ridesData } = await supabase
        .from('rides')
        .select('id, status')
        .eq('driver_id', userId)
        .eq('status', 'completed');
      
      // Get user rating
      const { data: reviewsData } = await supabase
        .from('reviews')
        .select('rating')
        .in('ride_id', ridesData?.map(r => r.id) || []);

      const earnedBadgeTypes = userBadges.map(b => b.badge_type);
      const progress = {};
      
      // Calculate progress for each badge type
      if (!earnedBadgeTypes.includes('century_club')) {
        const completedRides = ridesData?.length || 0;
        progress.century_club = {
          current: completedRides,
          target: 100,
          percentage: Math.min((completedRides / 100) * 100, 100)
        };
      }
      
      if (!earnedBadgeTypes.includes('super_active')) {
        const totalPosts = postsData?.length || 0;
        progress.super_active = {
          current: totalPosts,
          target: 50,
          percentage: Math.min((totalPosts / 50) * 100, 100)
        };
      }
      
      if (!earnedBadgeTypes.includes('package_pro')) {
        const totalPackages = packagesData?.length || 0;
        progress.package_pro = {
          current: totalPackages,
          target: 50,
          percentage: Math.min((totalPackages / 50) * 100, 100)
        };
      }
      
      if (!earnedBadgeTypes.includes('five_star_driver')) {
        const avgRating = reviewsData?.length > 0 
          ? reviewsData.reduce((sum, r) => sum + r.rating, 0) / reviewsData.length 
          : 0;
        const reviewCount = reviewsData?.length || 0;
        
        if (reviewCount >= 20) {
          progress.five_star_driver = {
            current: avgRating.toFixed(1),
            target: 5.0,
            percentage: Math.min((avgRating / 5.0) * 100, 100)
          };
        } else {
          progress.five_star_driver = {
            current: reviewCount,
            target: 20,
            percentage: Math.min((reviewCount / 20) * 100, 100),
            note: 'Need 20+ reviews first'
          };
        }
      }
      
      if (!earnedBadgeTypes.includes('explorer')) {
        const uniqueCities = new Set(
          postsData?.filter(p => p.address_to_city).map(p => p.address_to_city) || []
        ).size;
        progress.explorer = {
          current: uniqueCities,
          target: 10,
          percentage: Math.min((uniqueCities / 10) * 100, 100)
        };
      }
      
      setBadgeProgress(progress);
      
    } catch (err) {
      console.error('Error calculating badge progress:', err);
    }
  };

  // Handle badge click
  const handleBadgeClick = (badgeType, badgeConfig) => {
    const badgeName = t(`badges.badge_names.${badgeType}`, badgeConfig.name);
    const badgeDescription = t(`badges.badge_descriptions.${badgeType}`, badgeConfig.description);
    
    addToast({
      type: 'info',
      title: badgeName,
      description: badgeDescription,
      duration: 4000
    });
  };

  // Refresh badges (for when user might have earned new ones)
  const refreshBadges = async () => {
    try {
      // Check for new activity-based badges
      await supabase.rpc('check_activity_badges', { p_user_id: userId });
      
      // Refetch badges
      const { data: badgesData, error: badgesError } = await supabase
        .from('user_badges')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('earned_at', { ascending: false });
        
      if (!badgesError && badgesData) {
        setUserBadges(badgesData);
        await calculateBadgeProgress(userId);
        
        addToast({
          type: 'success',
          title: t('badges.badges_updated'),
          description: t('badges.badges_check_complete')
        });
      }
    } catch (err) {
      console.error('Error refreshing badges:', err);
      addToast({
      type: 'error',
      title: t('errors.badge_refresh_failed'),
      description: err.message
      });
    }
  };

  if (isLoading) {
    return (
      <div className="user-stats">
        <div className="loading-container">
          <Spinner size="medium" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-stats">
        <h2>{t('badges.your_badges')}</h2>
        <div className="error-message">
          {t('errors.badges_load_failed')}: {error}
        </div>
      </div>
    );
  }

  const earnedBadges = userBadges.map(b => b.badge_type);
  const availableProgressBadges = Object.keys(badgeProgress);
  const visibleBadges = showAll ? earnedBadges : earnedBadges.slice(0, 6);

  return (
    <div className="user-stats user-badges-section">
      <div className="badges-header">
        <h2>{t('badges.your_badges')} ({earnedBadges.length})</h2>
        <button 
          className="refresh-badges-btn"
          onClick={refreshBadges}
          title={t('badges.check_new_badges')}
        >
          <LuRefreshCw />
        </button>
      </div>
      
      {/* Earned Badges */}
      {earnedBadges.length > 0 ? (
        <div className="badges-section">
          <h3>{t('badges.earned_badges')}</h3>
          <div className="earned-badges-grid">
            {visibleBadges.map((badgeType, index) => {
              const badge = userBadges.find(b => b.badge_type === badgeType);
              return (
                <div key={badgeType} className="badge-item earned">
                  <UserBadge
                    badgeType={badgeType}
                    size="large"
                    onClick={handleBadgeClick}
                    showTooltip={false}
                  />
                  <div className="badge-info">
                    <h4>{t(`badges.badge_names.${badgeType}`, BADGE_CONFIG[badgeType]?.name || badgeType)}</h4>
                    <p className="badge-description">
                      {t(`badges.badge_descriptions.${badgeType}`, BADGE_CONFIG[badgeType]?.description || 'Achievement unlocked!')}
                    </p>
                    <p className="badge-earned-date">
                      {t('badges.earned')}: {new Date(badge.earned_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          
          {earnedBadges.length > 6 && (
            <button 
              className="show-all-badges-btn"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? t('badges.show_less') : t('badges.show_all')} ({earnedBadges.length})
            </button>
          )}
        </div>
      ) : (
        <div className="no-badges">
          <p>{t('badges.no_badges_yet')}</p>
          <p>{t('badges.start_earning_hint')}</p>
        </div>
      )}

      {/* Badge Progress */}
      {availableProgressBadges.length > 0 && (
        <div className="badges-section">
          <h3>{t('badges.badge_progress')}</h3>
          <div className="badge-progress-grid">
            {availableProgressBadges.map(badgeType => {
              const progress = badgeProgress[badgeType];
              const config = BADGE_CONFIG[badgeType];
              
              if (!config) return null;
              
              return (
                <div key={badgeType} className="badge-progress-item">
                  <div className="progress-badge">
                    <UserBadge
                      badgeType={badgeType}
                      size="medium"
                      onClick={handleBadgeClick}
                      showTooltip={false}
                      style={{ opacity: 0.6 }}
                    />
                  </div>
                  <div className="progress-info">
                    <h5>{t(`badges.badge_names.${badgeType}`, config.name)}</h5>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${progress.percentage}%` }}
                      />
                    </div>
                    <div className="progress-text">
                      {progress.note || `${progress.current} / ${progress.target}`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Stats for Badge Context */}
      <div className="badge-quick-stats">
        <div className="quick-stat">
          <span className="stat-value">{earnedBadges.length}</span>
          <span className="stat-label">{t('badges.total_badges')}</span>
        </div>
        <div className="quick-stat">
          <span className="stat-value">{availableProgressBadges.length}</span>
          <span className="stat-label">{t('badges.in_progress')}</span>
        </div>
        <div className="quick-stat">
          <span className="stat-value">{Object.keys(BADGE_CONFIG).length}</span>
          <span className="stat-label">{t('badges.total_available')}</span>
        </div>
      </div>
    </div>
  );
};

export default UserBadges;